/**
 * Utility functions for PO Lines Tab
 */
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { ACKNOWLEDGE_STATUS_CONFIG, PROCESSING_STAGE_CONFIG, StatusConfig } from './types';
import type { User } from '../../../../lib/api';

/**
 * Get sort icon based on current sort state
 */
export function getSortIcon(
  field: string,
  sortField: string | null,
  sortDirection: 'asc' | 'desc'
) {
  if (sortField !== field) {
    return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
  }
  return sortDirection === 'asc' ? (
    <ArrowUp className="h-3 w-3 text-primary" />
  ) : (
    <ArrowDown className="h-3 w-3 text-primary" />
  );
}

/**
 * Get acknowledge status badge element
 */
export function getAcknowledgeStatusBadge(status: string) {
  const config: StatusConfig = ACKNOWLEDGE_STATUS_CONFIG[status] || {
    label: status || 'Unknown',
    className: 'bg-gray-100 text-gray-800'
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

/**
 * Get DNA-driven processing-stage badge element
 * (Docked / WIP / Completed / Shipped / On Hold / Cancelled).
 */
export function getProcessingStageBadge(stage: string) {
  const config: StatusConfig = PROCESSING_STAGE_CONFIG[stage] || {
    label: stage || '-',
    className: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

/**
 * Check if user can release a hold on a line.
 * Admins and staff can always release holds.
 * Suppliers and customers can release holds they placed.
 * Falls back to true if no user is provided (backwards compatibility).
 */
export function canReleaseHold(user?: User | null): boolean {
  if (!user) return true;

  // Staff and superusers can always release holds
  if (user.is_staff || user.is_superuser) return true;

  // Users with admin or supplier platform roles can release
  if (user.platform === 'SUPPLIER' || user.platform === 'HELIX') return true;

  // Customers can release holds they are allowed to
  if (user.platform === 'CUSTOMER') return true;

  // Default: allow release (graceful fallback)
  return true;
}

/**
 * Priority pill for the PO-lines grid, or a muted dash when the line has none.
 *
 * @param priority - line priority (critical / high / medium / low), if any
 * @returns the badge element
 */
export function getPriorityBadge(priority?: string | null) {
  if (!priority) return <span className="text-muted-foreground">-</span>;
  const tone =
    priority === 'critical' ? 'bg-red-100 text-red-800' :
    priority === 'high' ? 'bg-orange-100 text-orange-800' :
    priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
    'bg-gray-100 text-gray-800';
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${tone}`}>
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </span>
  );
}
